import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiJson } from '@/lib/api';

const STATUS_QUERY_KEY = ['admin', 'professional-invite-reminders', 'status'] as const;
const RECENT_SENDS_QUERY_KEY = [
  'admin',
  'professional-invite-reminders',
  'recent-sends',
] as const;

function formatRelativeSeconds(totalSeconds: number): string {
  if (totalSeconds <= 0) return 'now';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return `${hours}h ${remMinutes}m`;
  }
  if (minutes >= 1) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

function formatEmailKey(key: string): string {
  if (key === 'initial') return 'Day 0 (invite)';
  return key.replace('reminder_day_', 'Day ');
}

type ProfessionalInviteReminderStatus = {
  campaign_name: string;
  schedule_environment: string;
  schedule_description: string;
  next_run_at_utc: string | null;
  next_run_in_seconds: number | null;
  mailgun_sends_enabled: boolean;
  pending_invites: number;
  invites_by_status: Record<string, number>;
  next_run_will_send_count: number;
  upcoming_sends: {
    invite_id: number;
    professional_email: string;
    coparent_name: string | null;
    invite_created_at: string;
    invite_age_days: number;
    next_email_key: string;
    reminders_already_sent: string[];
  }[];
  pending_not_queued: {
    invite_id: number;
    professional_email: string;
    coparent_name: string | null;
    invite_created_at: string;
    invite_age_days: number;
    reason: string;
    next_email_key: string | null;
    days_until_next_email: number | null;
    eligible_at_utc: string | null;
    reminders_already_sent: string[];
  }[];
  step_eligible: {
    email_key: string;
    delay_days: number;
    eligible_count: number;
  }[];
  cadence: {
    email_key: string;
    delay_days: number;
    subject_template: string;
  }[];
  reminder_sends_last_7_days: number;
  reminder_sends_last_30_days: number;
  reminder_sends_all_time: number;
  initial_sends_all_time: number;
  skipped_invalid_recipient_all_time: number;
  pending_in_drip: number;
  pending_drip_complete: number;
  pending_never_reminded: number;
  pending_accepted_all_time: number;
};

type RecentSendRow = {
  id: number;
  invite_id: number;
  professional_email: string;
  email_key: string;
  sent_at: string;
  mailgun_message_id: string | null;
  invite_status: string | null;
};

function AdminProInviteRemindersPage() {
  const { getToken } = useAuth();

  const statusQuery = useQuery({
    queryKey: STATUS_QUERY_KEY,
    queryFn: () =>
      apiJson<ProfessionalInviteReminderStatus>(
        getToken,
        '/v1/admin/professional-invite-reminders/status',
      ),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const recentSendsQuery = useQuery({
    queryKey: RECENT_SENDS_QUERY_KEY,
    queryFn: () =>
      apiJson<RecentSendRow[]>(
        getToken,
        '/v1/admin/professional-invite-reminders/recent-sends?limit=50',
      ),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const serverNextRunSeconds = statusQuery.data?.next_run_in_seconds ?? null;
  const [secondsUntilNextRun, setSecondsUntilNextRun] = useState<number | null>(
    serverNextRunSeconds,
  );
  useEffect(() => {
    setSecondsUntilNextRun(serverNextRunSeconds);
  }, [serverNextRunSeconds]);
  useEffect(() => {
    if (serverNextRunSeconds === null) return;
    const id = window.setInterval(() => {
      setSecondsUntilNextRun((prev) =>
        prev === null ? null : Math.max(0, prev - 1),
      );
    }, 1000);
    return () => window.clearInterval(id);
  }, [serverNextRunSeconds]);

  const status = statusQuery.data;

  return (
    <div className='space-y-8'>
      <div>
        <h1 className='text-2xl font-semibold text-gray-900'>
          Professional invite reminders
        </h1>
        <p className='mt-2 text-sm text-gray-600'>
          Read-only view of the coparent → professional email drip (day 3, 5, 7,
          14, 21 after the initial invite). Invite codes from professionals are out
          of scope. At most one reminder per invite per Celery run; backlogged
          invites drain in order.
        </p>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <button
          type='button'
          onClick={() => {
            void statusQuery.refetch();
            void recentSendsQuery.refetch();
          }}
          className='px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50'
        >
          Refresh
        </button>
        {statusQuery.isFetching ? (
          <span className='text-xs text-gray-500'>Updating…</span>
        ) : null}
      </div>

      {statusQuery.isLoading ? (
        <div className='flex justify-center py-16'>
          <div className='animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full' />
        </div>
      ) : statusQuery.isError ? (
        <p className='text-red-600 text-sm'>Could not load status.</p>
      ) : status ? (
        <>
          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3'>
            <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Next run blast
              </p>
              <p className='mt-1 text-3xl font-semibold tabular-nums text-gray-900'>
                {status.next_run_will_send_count.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                Professionals who will get exactly one reminder email on the next
                beat tick
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Next Celery run
              </p>
              <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                {status.schedule_environment === 'production' &&
                secondsUntilNextRun !== null
                  ? `in ${formatRelativeSeconds(secondsUntilNextRun)}`
                  : status.schedule_description}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                {status.next_run_at_utc
                  ? `${new Date(status.next_run_at_utc).toLocaleString()} (local) · ${status.schedule_description}`
                  : status.schedule_description}
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Pending invites
              </p>
              <p className='mt-1 text-3xl font-semibold tabular-nums text-gray-900'>
                {status.pending_invites.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                {status.pending_never_reminded} never reminded ·{' '}
                {status.pending_in_drip} in drip ·{' '}
                {status.pending_drip_complete} at final step sent
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Mailgun (prod)
              </p>
              <p className='mt-1 text-sm font-semibold text-gray-900'>
                {status.mailgun_sends_enabled ? 'Live sends' : 'Dry log only'}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                {status.schedule_environment === 'production'
                  ? 'ENV=production'
                  : `ENV=${status.schedule_environment}`}
              </p>
            </div>
          </div>

          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2'>
            {(
              [
                ['Reminders (7d)', status.reminder_sends_last_7_days],
                ['Reminders (30d)', status.reminder_sends_last_30_days],
                ['Reminders (all)', status.reminder_sends_all_time],
                ['Initial invites logged', status.initial_sends_all_time],
                ['Accepted (all time)', status.pending_accepted_all_time],
              ] as const
            ).map(([label, val]) => (
              <div
                key={label}
                className='bg-white rounded-lg border border-gray-200 px-3 py-3 shadow-sm'
              >
                <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight'>
                  {label}
                </p>
                <p className='mt-1 text-lg font-semibold tabular-nums text-gray-900'>
                  {val.toLocaleString()}
                </p>
              </div>
            ))}
            <div className='bg-white rounded-lg border border-gray-200 px-3 py-3 shadow-sm'>
              <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight'>
                Skipped (invalid email)
              </p>
              <p className='mt-1 text-lg font-semibold tabular-nums text-gray-900'>
                {status.skipped_invalid_recipient_all_time.toLocaleString()}
              </p>
            </div>
          </div>

          <section className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
            <div className='px-4 py-3 border-b border-gray-200'>
              <h2 className='text-lg font-semibold text-gray-900'>
                Eligible by step (raw)
              </h2>
              <p className='text-xs text-gray-500 mt-1'>
                Count per milestone before the one-per-invite cap. Next run uses
                the earliest missing step per invite.
              </p>
            </div>
            <div className='overflow-x-auto'>
              <table className='min-w-full text-sm'>
                <thead className='bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500'>
                  <tr>
                    <th className='px-4 py-2'>Step</th>
                    <th className='px-4 py-2'>Delay</th>
                    <th className='px-4 py-2'>Eligible now</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {status.step_eligible.map((row) => (
                    <tr key={row.email_key}>
                      <td className='px-4 py-2 font-medium text-gray-900'>
                        {formatEmailKey(row.email_key)}
                      </td>
                      <td className='px-4 py-2 text-gray-600'>
                        ≥ {row.delay_days} days
                      </td>
                      <td className='px-4 py-2 tabular-nums'>{row.eligible_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
            <div className='px-4 py-3 border-b border-gray-200'>
              <h2 className='text-lg font-semibold text-gray-900'>
                Cadence (subject templates)
              </h2>
            </div>
            <ul className='divide-y divide-gray-100 text-sm'>
              <li className='px-4 py-2 text-gray-600'>
                <span className='font-medium text-gray-900'>Day 0</span> — sent on
                invite create (not part of this Celery task)
              </li>
              {status.cadence.map((step) => (
                <li key={step.email_key} className='px-4 py-2'>
                  <span className='font-medium text-gray-900'>
                    {formatEmailKey(step.email_key)}
                  </span>
                  <span className='text-gray-500'> (≥ {step.delay_days}d)</span>
                  <span className='text-gray-600'> — {step.subject_template}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
            <div className='px-4 py-3 border-b border-gray-200'>
              <h2 className='text-lg font-semibold text-gray-900'>
                Next run queue
              </h2>
              <p className='text-xs text-gray-500 mt-1'>
                {status.next_run_will_send_count} of {status.pending_invites} pending
                invite(s) will get a reminder on the next run (same order as the
                task). Others are listed below.
              </p>
            </div>
            {status.upcoming_sends.length === 0 ? (
              <p className='px-4 py-6 text-sm text-gray-500'>
                No reminders queued for the next run.
              </p>
            ) : (
              <div className='overflow-x-auto'>
                <table className='min-w-full text-sm'>
                  <thead className='bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500'>
                    <tr>
                      <th className='px-4 py-2'>Professional</th>
                      <th className='px-4 py-2'>Coparent</th>
                      <th className='px-4 py-2'>Age</th>
                      <th className='px-4 py-2'>Next email</th>
                      <th className='px-4 py-2'>Already sent</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100'>
                    {status.upcoming_sends.map((row) => (
                      <tr key={row.invite_id}>
                        <td className='px-4 py-2 text-gray-900'>
                          {row.professional_email}
                        </td>
                        <td className='px-4 py-2 text-gray-600'>
                          {row.coparent_name ?? '—'}
                        </td>
                        <td className='px-4 py-2 tabular-nums text-gray-600'>
                          {row.invite_age_days}d
                        </td>
                        <td className='px-4 py-2 font-medium text-primary'>
                          {formatEmailKey(row.next_email_key)}
                        </td>
                        <td className='px-4 py-2 text-gray-500 text-xs'>
                          {row.reminders_already_sent.length > 0
                            ? row.reminders_already_sent
                                .map(formatEmailKey)
                                .join(', ')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className='bg-white rounded-lg border border-amber-200 bg-amber-50/40 shadow-sm overflow-hidden'>
            <div className='px-4 py-3 border-b border-amber-200/80'>
              <h2 className='text-lg font-semibold text-gray-900'>
                Pending, not on next run
              </h2>
              <p className='text-xs text-gray-600 mt-1'>
                Still <span className='font-medium'>pending</span> in the database but
                excluded from the next blast — usually because the invite is not old
                enough for the next reminder step yet.
              </p>
            </div>
            {status.pending_not_queued.length === 0 ? (
              <p className='px-4 py-6 text-sm text-gray-600'>
                Every pending invite is in the next run queue (or there are no pending
                invites).
              </p>
            ) : (
              <div className='overflow-x-auto'>
                <table className='min-w-full text-sm'>
                  <thead className='bg-amber-50/80 text-left text-xs font-semibold uppercase text-gray-600'>
                    <tr>
                      <th className='px-4 py-2'>Professional</th>
                      <th className='px-4 py-2'>Coparent</th>
                      <th className='px-4 py-2'>Age</th>
                      <th className='px-4 py-2'>Next step</th>
                      <th className='px-4 py-2'>Why not queued</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-amber-100 bg-white/60'>
                    {status.pending_not_queued.map((row) => (
                      <tr key={row.invite_id}>
                        <td className='px-4 py-2 text-gray-900 align-top'>
                          {row.professional_email}
                        </td>
                        <td className='px-4 py-2 text-gray-600 align-top'>
                          {row.coparent_name ?? '—'}
                        </td>
                        <td className='px-4 py-2 tabular-nums text-gray-600 align-top whitespace-nowrap'>
                          {row.invite_age_days}d
                        </td>
                        <td className='px-4 py-2 align-top whitespace-nowrap'>
                          {row.next_email_key ? (
                            <span className='font-medium text-gray-800'>
                              {formatEmailKey(row.next_email_key)}
                              {row.days_until_next_email != null &&
                              row.days_until_next_email > 0 ? (
                                <span className='block text-xs font-normal text-amber-800 mt-0.5'>
                                  in ~{row.days_until_next_email}d
                                  {row.eligible_at_utc
                                    ? ` (${new Date(row.eligible_at_utc).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`
                                    : null}
                                </span>
                              ) : null}
                            </span>
                          ) : (
                            <span className='text-gray-500'>—</span>
                          )}
                        </td>
                        <td className='px-4 py-2 text-gray-700 align-top max-w-md'>
                          {row.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {Object.keys(status.invites_by_status).length > 0 ? (
            <section className='bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-3'>
              <h2 className='text-sm font-semibold text-gray-900'>
                All invites by status
              </h2>
              <dl className='mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm'>
                {Object.entries(status.invites_by_status)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([st, count]) => (
                    <div key={st} className='flex gap-2'>
                      <dt className='text-gray-500'>{st}</dt>
                      <dd className='font-semibold tabular-nums text-gray-900'>
                        {count}
                      </dd>
                    </div>
                  ))}
              </dl>
            </section>
          ) : null}
        </>
      ) : null}

      <section className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
        <div className='px-4 py-3 border-b border-gray-200'>
          <h2 className='text-lg font-semibold text-gray-900'>Recent sends</h2>
          <p className='text-xs text-gray-500 mt-1'>
            Latest rows from professional_invite_email_sends (initial + reminders).
          </p>
        </div>
        {recentSendsQuery.isLoading ? (
          <p className='px-4 py-6 text-sm text-gray-500'>Loading…</p>
        ) : recentSendsQuery.isError ? (
          <p className='px-4 py-6 text-sm text-red-600'>Could not load sends.</p>
        ) : !recentSendsQuery.data?.length ? (
          <p className='px-4 py-6 text-sm text-gray-500'>No sends logged yet.</p>
        ) : (
          <div className='overflow-x-auto'>
            <table className='min-w-full text-sm'>
              <thead className='bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500'>
                <tr>
                  <th className='px-4 py-2'>Sent</th>
                  <th className='px-4 py-2'>Key</th>
                  <th className='px-4 py-2'>Professional</th>
                  <th className='px-4 py-2'>Invite status</th>
                  <th className='px-4 py-2'>Mailgun id</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {recentSendsQuery.data.map((row) => (
                  <tr key={row.id}>
                    <td className='px-4 py-2 text-gray-600 whitespace-nowrap'>
                      {new Date(row.sent_at).toLocaleString()}
                    </td>
                    <td className='px-4 py-2 font-medium text-gray-900'>
                      {formatEmailKey(row.email_key)}
                    </td>
                    <td className='px-4 py-2 text-gray-900'>
                      {row.professional_email}
                    </td>
                    <td className='px-4 py-2 text-gray-600'>
                      {row.invite_status ?? '—'}
                    </td>
                    <td className='px-4 py-2 text-xs text-gray-500 font-mono max-w-[12rem] truncate'>
                      {row.mailgun_message_id ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export const Route = createFileRoute('/admin/pro-invite-reminders')({
  component: AdminProInviteRemindersPage,
});
